/**
 * ConditionsPanel — the four security axioms A1–A4.
 *
 * Each condition is rendered as a card from conditions.json, mapped to the
 * generator stratum it governs (G1–G4), the matrix operation that verifies it,
 * its plain-English meaning, the NTs it neutralizes, and the adversarial
 * fail-fast signature when it does not hold.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import conditionsData from "../data/conditions.json" with { type: "json" };
const TOKEN_COLOR: Record<string, string> = {
  a1: panelTokens.a1,
  a2: panelTokens.a2,
  a3: panelTokens.a3,
  a4: panelTokens.a4,
};
export function ConditionsPanel(): React.JSX.Element {
  return (
    <PanelSurface.PanelShell
      title="The Four Conditions"
      subtitle="A1–A4 are security axioms: they define the structural form that makes the ten vulnerability classes inexpressible. Each governs one generator stratum."
      accentColor={panelTokens.accentBlue}
    >
      <PanelSurface.Callout
        title="These are security axioms, not runtime checks"
        tone="info"
      >
        A1–A4 are the structural conditions a system must satisfy to inherit the
        CSK guarantee. They are verified once, on the matrix representation.
        Once they hold, the ten Neutralization Theorems hold by construction —
        there is no per-request guard.
      </PanelSurface.Callout>

      <div className="mt-4 grid grid-cols-1 gap-3.5">
        {conditionsData.conditions.map((c) => {
          const color = TOKEN_COLOR[c.token] ?? panelTokens.accent;
          return (
            <div
              key={c.id}
              className="rounded-[14px] border border-[var(--mit-border)] bg-[rgba(5,7,16,0.5)] px-5 py-4"
              style={{ borderLeft: `3px solid ${color}` }}
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <span
                  className="font-mono text-[15px] font-bold"
                  style={{ color }}
                >
                  {c.id}
                </span>
                <span className="text-[14.5px] font-semibold text-[var(--mit-bright)]">
                  {c.name}
                </span>
                {PanelSurface.badge(`${c.generator} · ${c.generatorName}`, color)}
              </div>

              <div className="mb-2.5 font-mono text-[12.5px] text-[var(--mit-a3)]">
                {c.operation}
              </div>

              <PanelSurface.Prose className="m-0 mb-3">{c.meaning}</PanelSurface.Prose>

              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-[11px] font-bold uppercase tracking-[0.08em] text-[var(--mit-muted)]">
                  Neutralizes
                </span>
                {c.neutralizes.map((nt) => (
                  <span key={nt}>{PanelSurface.badge(nt, panelTokens.immuneGreen)}</span>
                ))}
              </div>

              <div className="text-[12px] leading-[1.6] text-[var(--mit-muted)]">
                <span className="font-bold uppercase tracking-[0.08em] text-[var(--mit-singular-amber)]">
                  Fail signature:{" "}
                </span>
                {c.failSignature}
              </div>
            </div>
          );
        })}
      </div>

      <PanelSurface.SectionHeading>The block-diagonal picture</PanelSurface.SectionHeading>
      <PanelSurface.Callout title="Independent strata" tone="safe">
        In the joint embedding φ(m) = diag(φ₁, φ₂, φ₃, φ₄), the four generators
        occupy independent diagonal summands. The zero off-diagonal blocks are
        the matrix proof that a violation in one governance dimension cannot
        propagate to another — authority cannot corrupt knowledge, temporal
        state cannot touch resource accounting.
      </PanelSurface.Callout>

      <PanelSurface.RepositoryLinks
        items={[
          {
            label:
              "MATRIX-VERIFICATION-SECURITY-GUIDE.md (A1–A4 audit checklist)",
            path: "theory/structural/MATRIX-VERIFICATION-SECURITY-GUIDE.md",
          },
          {
            label: "RD-56 §Formal statement",
            path: "research/settled/RD-56-MATRIX-IMMUNITY-THEOREM.md",
          },
        ]}
      />
    </PanelSurface.PanelShell>
  );
}
